import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { TreeNode } from '../../types';
import { TreeViewNode } from '../../components/TreeView/TreeView';
import * as atoms from '../../atoms';
import {
  collectLeafIds,
  excludeExposures,
  excludeTestsAndExposures,
  filterTree,
  formatTreeData,
  getAllSubNodes,
  getFolderNodeIds,
  getItemLabel,
  includeModelsAndSources,
  includeModelsOnly,
  includeModelsSourcesTablesAndViews,
  selectSidebarData,
} from './utils';
import { BuildLeaf, TreeDataProps, TreeDataResult, TreeViews } from './types';

const useOSSTreeData = ({
  reportData,
  searchValue,
  treeType,
  treeViewBy,
}: TreeDataProps): TreeDataResult => {
  const setCurrentTreeData = useSetRecoilState(atoms.currentTreeData);
  const setCurrentTreeNodesById = useSetRecoilState(atoms.currentTreeNodesById);
  const { pathname } = useLocation();

  const treeViewData = useMemo(
    () =>
      reportData?.groups?.[treeType]?.find((view) => view.name === treeViewBy)
        ?.data,
    [reportData, treeType, treeViewBy],
  );

  const totals = useMemo(() => {
    switch (pathname) {
      case '/report/test-results':
      case '/report/lineage':
      case '/test-management':
        return reportData?.test_results_totals;
      case '/report/test-runs':
        return reportData?.test_runs_totals;
      case '/report/model-runs':
        return reportData?.model_runs_totals;
      default:
        return reportData?.test_results_totals;
    }
  }, [pathname, reportData]);

  const testsData = useMemo(() => {
    switch (pathname) {
      case '/report/test-results':
        return reportData?.test_results;
      case '/report/test-runs':
        return reportData?.test_runs;
      default:
        return undefined;
    }
  }, [pathname, reportData]);

  const variants = useMemo(
    () => ({
      noExposures: excludeExposures(treeViewData),
      noTestsNoExposures: excludeTestsAndExposures(treeViewData),
      noTestsNoEphemeral: filterTree(
        treeViewData,
        (leaf) =>
          leaf.resource_type !== 'test' &&
          !(
            leaf.resource_type === 'model' &&
            reportData?.models?.[leaf.node_id]?.materialization === 'ephemeral'
          ),
      ),
      modelsAndSources: includeModelsAndSources(treeViewData),
      modelsOnly: includeModelsOnly(treeViewData),
      modelsSourcesTablesAndViews:
        includeModelsSourcesTablesAndViews(treeViewData),
    }),
    [treeViewData, reportData],
  );

  const selectedSidebarData = useMemo(
    () => selectSidebarData(pathname, variants, treeViewData),
    [pathname, variants, treeViewData],
  );

  const treePathSeparator = treeViewBy === 'dwh' ? '.' : '/';

  const buildReportLeaf: BuildLeaf = useMemo(
    () => (leaf) => ({
      label: getItemLabel(
        leaf,
        reportData?.models,
        testsData,
        treeViewBy === 'dwh',
      ),
      totals: totals?.[leaf.node_id],
    }),
    [reportData, testsData, totals, treeViewBy],
  );

  const treeViews: TreeViews | undefined = useMemo(() => {
    if (!selectedSidebarData) return undefined;
    const sidebar = selectedSidebarData as TreeNode;
    return Object.keys(sidebar)
      .filter((key) => sidebar[key] != null)
      .map((key) =>
        formatTreeData(
          `folderNode_${key}`,
          key,
          sidebar[key],
          searchValue,
          buildReportLeaf,
          0,
          treePathSeparator,
          undefined,
        ),
      );
  }, [selectedSidebarData, searchValue, buildReportLeaf, treePathSeparator]);

  const folderNodeIds = useMemo(
    () => treeViews?.map((tree) => getFolderNodeIds(tree)).flat() || [],
    [treeViews],
  );

  const treeViewsLeafIds = useMemo(
    () => (selectedSidebarData ? collectLeafIds(selectedSidebarData) : []),
    [selectedSidebarData],
  );

  useEffect(() => {
    setCurrentTreeData(treeViews);
    const treeNodesById: Record<string, TreeViewNode> = {};
    getAllSubNodes(treeViews || []).forEach((node) => {
      treeNodesById[node.id] = node;
    });
    setCurrentTreeNodesById(treeNodesById);
  }, [treeViews, setCurrentTreeData, setCurrentTreeNodesById]);

  return {
    treeViews,
    folderNodeIds,
    treeViewsLeafIds,
    isTreeDataLoading: false,
    hasBiData: false,
  };
};

export default useOSSTreeData;
